import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../utils/api';
import { formatDate, getStatusBadgeClass, formatStatusText } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminTableLoadingOverlay from '../../components/admin/AdminTableLoadingOverlay';
import { useDebounce } from '../../hooks/useDebounce';

const FETCH_LIMIT = 500;

const AdminKYC = () => {
  const [rawKycList, setRawKycList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    fetchKYCList();
  }, [debouncedSearch]);

  const fetchKYCList = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getKYCList({
        search: debouncedSearch || undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      setRawKycList(response.data.kyc_documents);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load KYC list');
    } finally {
      setLoading(false);
    }
  };

  const filteredKycList = statusFilter
    ? rawKycList.filter((k) => k.status === statusFilter)
    : rawKycList;
  const total = filteredKycList.length;
  const kycList = filteredKycList.slice((page - 1) * pageSize, page * pageSize);

  const handleKYCAction = async (kycId, status) => {
    try {
      await adminAPI.kycAction({ kyc_id: kycId, status, review_notes: reviewNotes });
      toast.success('KYC updated');
      setSelectedKYC(null);
      setReviewNotes('');
      fetchKYCList();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const filterOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_review', label: 'In Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'resubmit', label: 'Resubmit' },
  ];

  if (loading && rawKycList.length === 0) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-kyc">
      <h1 className="text-2xl sm:text-4xl font-bold text-foreground">KYC Approval</h1>

      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by user name or email..."
        filterValue={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); setPage(1); }}
        filterOptions={filterOptions}
        filterPlaceholder="All statuses"
        total={total}
        loading={loading}
      />

      <Card className="glass-card p-6 overflow-hidden">
        <AdminTableLoadingOverlay loading={loading}>
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">User</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Status</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Submitted</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {kycList.map((kyc) => (
                <tr key={kyc.id} className="table-row" data-testid={`kyc-row-${kyc.id}`}>
                  <td className="py-4">
                    <div>
                      <p className="text-foreground">{kyc.user?.full_name || '—'}</p>
                      <p className="text-muted-foreground text-sm">{kyc.user?.email || '—'}</p>
                    </div>
                  </td>
                  <td className="py-4">
                    <span className={`badge ${getStatusBadgeClass(kyc.status)}`}>
                      {formatStatusText(kyc.status)}
                    </span>
                  </td>
                  <td className="py-4 text-muted-foreground text-sm">{formatDate(kyc.submitted_at)}</td>
                  <td className="py-4">
                    {kyc.status === 'in_review' && (
                      <Button size="sm" onClick={() => setSelectedKYC(kyc)} data-testid={`review-btn-${kyc.id}`}>
                        Review
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} total={total} onPageChange={setPage} loading={loading} />
        </AdminTableLoadingOverlay>
      </Card>

      <Dialog open={!!selectedKYC} onOpenChange={() => setSelectedKYC(null)}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-foreground">Review KYC</DialogTitle>
          </DialogHeader>
          {selectedKYC && (
            <div className="space-y-4">
              <div>
                <p className="text-muted-foreground text-sm">User</p>
                <p className="text-foreground">
                  {selectedKYC.user?.full_name || '—'} ({selectedKYC.user?.email || '—'})
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm mb-2">Review Notes</p>
                <Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} className="input-field" placeholder="Add review notes..." data-testid="review-notes-textarea" />
              </div>
              <div className="flex space-x-2">
                <Button onClick={() => handleKYCAction(selectedKYC.id, 'approved')} className="btn-primary" data-testid="approve-kyc-button">
                  Approve
                </Button>
                <Button onClick={() => handleKYCAction(selectedKYC.id, 'rejected')} className="bg-red-600 hover:bg-red-700" data-testid="reject-kyc-button">
                  Reject
                </Button>
                <Button onClick={() => handleKYCAction(selectedKYC.id, 'resubmit')} variant="outline" data-testid="resubmit-kyc-button">
                  Request Resubmit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminKYC;
